import { root } from '@/infrastructure/container';
import { TOKEN_ILogger, ConsoleLogger } from '@/services/Logger';
import { TOKEN_IMetrics, APP_METRICS, MetricsRegistry } from '@/services/Metrics';
import { TOKEN_ITrace, ConsoleTraceSink, Trace } from '@/services/Trace';
import { TOKEN_IClock, SystemClock } from '@/services/Clock';
import { TOKEN_IIdGen, UuidV7Gen } from '@/services/IdGen';
import { TOKEN_IAppBus, AppBus } from '@/services/AppBus';
import { TOKEN_BusNarrator, BusNarrator } from '@/services/BusNarrator';
import { TOKEN_IDomConnectivity, DomConnectivity } from '@/services/DomConnectivity';
import { TOKEN_IDomNavigation, DomNavigation } from '@/services/DomNavigation';
import { TOKEN_IDomStorage, LocalDomStorage } from '@/services/DomStorage';
import { TOKEN_IHasher, WebCryptoHasher } from '@/services/Hasher';
import { TOKEN_IIdentity, WebCryptoIdentity } from '@/services/Identity';
import { TOKEN_ICeleriantClient, CeleriantClient } from '@/services/CeleriantClient';
import { TOKEN_ISleeper, TimerSleeper } from '@/services/Sleeper';
import {
  TOKEN_ISessionConnection,
  TOKEN_SessionGateway,
  SessionGateway,
} from '@/services/SessionGateway';
import { TOKEN_ProjectGateway, ProjectGateway } from '@/services/ProjectGateway';
import { TOKEN_IDomDownload, AnchorDomDownload } from '@/services/DomDownload';
import { TOKEN_ISessionFold, TOKEN_ReportGateway, ReportGateway } from '@/services/ReportGateway';
import { TOKEN_ITickGateway, TOKEN_TickEngine, TickEngine } from '@/services/TickEngine';
import { TOKEN_IAccessGateway, TOKEN_IAdminGateway } from '@/domain/accessGateway';
import { TOKEN_ICoachGateway } from '@/domain/coachGateway';
import { TOKEN_IFrontDeskGateway } from '@/domain/gateway';
import type { Court } from '@/domain/session';
import { TOKEN_AppStore, AppStore } from '@/views/AppStore';
import { TOKEN_CoachStoreFor, CoachStore } from '@/views/coach/CoachStore';
import { TOKEN_FrontDeskStore, FrontDeskStore } from '@/views/frontdesk/FrontDeskStore';
import { TOKEN_OnboardingStore, OnboardingStore } from '@/views/onboarding/OnboardingStore';
import { TOKEN_AdminAccessStore, AdminAccessStore } from '@/views/admin/AdminAccessStore';
import { TOKEN_AdminShellStore, AdminShellStore } from '@/views/admin/AdminShellStore';
import { TOKEN_MoverStore, MoverStore } from '@/views/mover/MoverStore';
import { TOKEN_IReportGateway } from '@/domain/reportGateway';
import { TOKEN_ReportStore, ReportStore } from '@/views/admin/ReportStore';

// The dependency graph, in one file, readable top to bottom. Every registration lives here;
// nothing self-registers. Singletons by default; transients (a store per list item) take a
// `() => Store` factory in the parent.
//
// Order within this function does not matter (resolution is lazy). Order in main.tsx does.
export function ud_di(): void {
  root.registerSingleton(TOKEN_ILogger, () => new ConsoleLogger());
  root.registerSingleton(TOKEN_IMetrics, () => new MetricsRegistry(APP_METRICS));
  root.registerSingleton(
    TOKEN_ITrace,
    (c) => new Trace(new ConsoleTraceSink(c.resolve(TOKEN_ILogger))),
  );
  root.registerSingleton(TOKEN_IClock, () => new SystemClock());
  root.registerSingleton(TOKEN_IIdGen, () => new UuidV7Gen());
  root.registerSingleton(
    TOKEN_IAppBus,
    (c) => new AppBus(c.resolve(TOKEN_ILogger), c.resolve(TOKEN_IMetrics)),
  );
  // Takes the concrete AppBus, not IAppBus: handlerCount is deliberately off the interface so an
  // injected store cannot branch on how many others are listening.
  root.registerSingleton(
    TOKEN_BusNarrator,
    (c) => new BusNarrator(c.resolve(TOKEN_IAppBus) as AppBus, c.resolve(TOKEN_ITrace)),
  );

  root.registerSingleton(
    TOKEN_IDomConnectivity,
    (c) => new DomConnectivity(c.resolve(TOKEN_IAppBus), c.resolve(TOKEN_ILogger)),
  );
  root.registerSingleton(
    TOKEN_IDomNavigation,
    (c) => new DomNavigation(c.resolve(TOKEN_IAppBus), c.resolve(TOKEN_ILogger)),
  );
  root.registerSingleton(TOKEN_IDomStorage, (c) => new LocalDomStorage(c.resolve(TOKEN_ILogger)));
  root.registerSingleton(
    TOKEN_IDomDownload,
    (c) => new AnchorDomDownload(c.resolve(TOKEN_ILogger)),
  );

  root.registerSingleton(TOKEN_IIdentity, () => new WebCryptoIdentity());
  root.registerSingleton(TOKEN_IHasher, () => new WebCryptoHasher());
  root.registerSingleton(
    TOKEN_ICeleriantClient,
    (c) =>
      new CeleriantClient(
        c.resolve(TOKEN_IIdentity),
        c.resolve(TOKEN_IClock),
        c.resolve(TOKEN_IMetrics),
      ),
  );

  root.registerSingleton(TOKEN_ISleeper, () => new TimerSleeper());

  // One gateway, two tokens. The concrete class is registered once and the two interfaces alias
  // it, so the front desk store and the shell hold the same handle and the same fold. Registering
  // it twice would open the session twice and split the state every screen reads.
  root.registerSingleton(
    TOKEN_SessionGateway,
    (c) =>
      new SessionGateway(
        c.resolve(TOKEN_ICeleriantClient),
        c.resolve(TOKEN_IAppBus),
        c.resolve(TOKEN_IDomConnectivity),
        c.resolve(TOKEN_IClock),
        c.resolve(TOKEN_ILogger),
      ),
  );
  root.registerSingleton(TOKEN_IFrontDeskGateway, (c) => c.resolve(TOKEN_SessionGateway));
  // The third token on the same instance. A coach's votes and a desk's registrations are writes on
  // ONE session aggregate, so they share one handle and one fold; the facades stay separate because
  // of what they let a caller do — the coach screen cannot see `registerPlayer` and the desk cannot
  // see the raw `execute`.
  root.registerSingleton(TOKEN_ICoachGateway, (c) => c.resolve(TOKEN_SessionGateway));
  root.registerSingleton(TOKEN_ISessionConnection, (c) => c.resolve(TOKEN_SessionGateway));
  // The FOURTH token on the same instance, for the admin laptop's tick engine. Same three members
  // as ICoachGateway, and the same one aggregate: the list the engine publishes has to land in the
  // fold the movers and coaches are reading.
  root.registerSingleton(TOKEN_ITickGateway, (c) => c.resolve(TOKEN_SessionGateway));
  // And the FIFTH, for the report. The narrowest of them: the fold and nothing else, because a
  // report reads a finished session and must not be able to write to one.
  root.registerSingleton(TOKEN_ISessionFold, (c) => c.resolve(TOKEN_SessionGateway));

  // Same shape for the project: one instance behind both access facades. A scanned phone resolves
  // IAccessGateway and cannot see a mint method; the admin laptop resolves IAdminGateway. Two
  // instances would open two project handles and split the share registry every screen reads.
  root.registerSingleton(
    TOKEN_ProjectGateway,
    (c) =>
      new ProjectGateway(
        c.resolve(TOKEN_ICeleriantClient),
        c.resolve(TOKEN_ISessionConnection),
        c.resolve(TOKEN_IClock),
        c.resolve(TOKEN_ILogger),
      ),
  );
  root.registerSingleton(TOKEN_IAccessGateway, (c) => c.resolve(TOKEN_ProjectGateway));
  root.registerSingleton(TOKEN_IAdminGateway, (c) => c.resolve(TOKEN_ProjectGateway));

  // Reporting owns no handle: the project fold comes from the gateway above and the session fold
  // from the one shared instance, so the CSV is built from exactly what this laptop is reading.
  root.registerSingleton(
    TOKEN_ReportGateway,
    (c) =>
      new ReportGateway(
        c.resolve(TOKEN_IAccessGateway),
        c.resolve(TOKEN_ISessionFold),
        c.resolve(TOKEN_ILogger),
      ),
  );
  root.registerSingleton(TOKEN_IReportGateway, (c) => c.resolve(TOKEN_ReportGateway));

  root.registerSingleton(
    TOKEN_OnboardingStore,
    (c) =>
      new OnboardingStore(
        c.resolve(TOKEN_IAccessGateway),
        c.resolve(TOKEN_IHasher),
        c.resolve(TOKEN_ILogger),
        // The two optionals. The P2 oracle pins the three-argument shape; production wants both,
        // because redeeming is a write on the grant table, the whole hall scans the same QR in the
        // same few seconds, and a shared jitter seed would have every phone retry in lockstep.
        c.resolve(TOKEN_ISleeper),
        c.resolve(TOKEN_IIdGen),
      ),
  );

  root.registerSingleton(
    TOKEN_AdminAccessStore,
    (c) =>
      new AdminAccessStore(
        c.resolve(TOKEN_IAdminGateway),
        c.resolve(TOKEN_IHasher),
        c.resolve(TOKEN_IIdGen),
        c.resolve(TOKEN_ILogger),
        // The optional fifth. The contract pins the four-argument shape, and production still wants
        // the jitter: two admin devices open on the same project would otherwise retry in lockstep.
        c.resolve(TOKEN_ISleeper),
        // The optional sixth: the local mirror that keeps the TV's codes across a reload of the
        // admin tab. Keys go to THIS device's storage only, never into an event or a log.
        c.resolve(TOKEN_IDomStorage),
      ),
  );

  // One tick engine, on whatever device resolves it. Constructing it arms NOTHING: the pulse is
  // installed by `startTicking()`, which only the admin shell calls, so a coach's phone holds an
  // inert engine and the hall keeps exactly one calculator (docs/rotation.md).
  root.registerSingleton(
    TOKEN_TickEngine,
    (c) =>
      new TickEngine(
        c.resolve(TOKEN_ITickGateway),
        c.resolve(TOKEN_IClock),
        c.resolve(TOKEN_ILogger),
        // The two optionals. Production injects the real generator and the real sleeper rather than
        // leaning on the engine's own defaults: two admin devices racing one tick would otherwise
        // retry in lockstep.
        c.resolve(TOKEN_IIdGen),
        c.resolve(TOKEN_ISleeper),
      ),
  );

  root.registerSingleton(
    TOKEN_AdminShellStore,
    (c) =>
      new AdminShellStore(
        c.resolve(TOKEN_IAccessGateway),
        c.resolve(TOKEN_AdminAccessStore),
        c.resolve(TOKEN_IDomStorage),
        c.resolve(TOKEN_IIdGen),
        c.resolve(TOKEN_IClock),
        c.resolve(TOKEN_IDomNavigation),
        c.resolve(TOKEN_ILogger),
        c.resolve(TOKEN_TickEngine),
      ),
  );

  // The report screen. One store, on the admin laptop, resolved by the Reports section of the admin
  // view and nowhere else.
  root.registerSingleton(
    TOKEN_ReportStore,
    (c) =>
      new ReportStore(
        c.resolve(TOKEN_IReportGateway),
        c.resolve(TOKEN_ILogger),
        // The optional third. The contract pins the two-argument shape; production still injects the
        // wrapper, because a store cannot touch `document` and a view may not hold the logic
        // (INVARIANTS rules 6 and 12).
        c.resolve(TOKEN_IDomDownload),
      ),
  );

  root.registerSingleton(
    TOKEN_AppStore,
    (c) =>
      new AppStore(
        c.resolve(TOKEN_ICeleriantClient),
        c.resolve(TOKEN_OnboardingStore),
        c.resolve(TOKEN_AdminShellStore),
        c.resolve(TOKEN_IAppBus),
        c.resolve(TOKEN_IDomConnectivity),
        c.resolve(TOKEN_IDomNavigation),
        c.resolve(TOKEN_ILogger),
      ),
  );

  // One coach store per COURT, which is the collection rule 5 names, and a lifetime decision the
  // composition root owns. It cannot be a plain singleton: the court is not known until a code has
  // been redeemed, and it cannot be a transient either, because the view resolves on every render
  // and a fresh store per render would drop the failure message a coach is still reading. So the
  // factory is a singleton and the instance it hands back for a court is the same one every time.
  // In production a device only ever asks for one court; P4's mover, which picks courts, is why the
  // map is a map.
  //
  // The map never evicts: a store made for a court lives as long as the app does.
  // The gateway is the shared one, so `rows` and the gate follow a session swap on their own while
  // `_failedMessage` does not — a banner from the previous session would still be on the screen.
  // A second session on one device is what would make that reachable, and it would need eviction
  // here rather than a change inside the store.
  root.registerSingleton(TOKEN_CoachStoreFor, (c) => {
    const byCourt = new Map<Court, CoachStore>();
    return (court: Court) => {
      const existing = byCourt.get(court);
      if (existing !== undefined) return existing;
      const store = new CoachStore(
        court,
        c.resolve(TOKEN_ICoachGateway),
        c.resolve(TOKEN_ILogger),
        // The optional fourth. The contract pins the three-argument shape; production still injects
        // the real generator rather than leaning on the store's own default.
        c.resolve(TOKEN_IIdGen),
      );
      byCourt.set(court, store);
      return store;
    };
  });

  // One mover store, a plain singleton. Unlike the coach's, nothing about it is keyed at runtime:
  // the court a mover is working is INTERNAL state they change by tapping, not an identity handed
  // to them by a redeemed code, so there is nothing here for a factory to key on and nothing to
  // evict. A mover roams; the store follows them.
  root.registerSingleton(
    TOKEN_MoverStore,
    (c) =>
      new MoverStore(
        c.resolve(TOKEN_ICoachGateway),
        c.resolve(TOKEN_ILogger),
        // The optional third. The contract pins the two-argument shape; production still injects
        // the real generator rather than leaning on the store's own default.
        c.resolve(TOKEN_IIdGen),
      ),
  );

  root.registerSingleton(
    TOKEN_FrontDeskStore,
    (c) =>
      new FrontDeskStore(
        c.resolve(TOKEN_IFrontDeskGateway),
        c.resolve(TOKEN_IIdGen),
        c.resolve(TOKEN_ISleeper),
        c.resolve(TOKEN_ILogger),
      ),
  );
}
